import { createContext, useContext } from "react";

// Product tour shared state (L0-U10 session 2, ADR 0031). The tour is an
// in-house step-highlight overlay anchored to the always-mounted sidebar nav
// (no new dependency, no route navigation). The overlay (ProductTour) and the
// highlighted nav items (Sidebar) read the same context so the active step's
// target gets a highlight ring while the overlay card describes it.

export interface TourStep {
  // Matches a `data-tour-id` attribute on a sidebar nav entry.
  targetId: string;
  title: string;
  body: string;
}

// The five primary surfaces from the L0-U10 spec, in nav order.
export const TOUR_STEPS: readonly TourStep[] = [
  {
    targetId: "dashboard",
    title: "Your dashboard",
    body: "Your performance at a glance — net P&L, win rate, equity curve, and any widgets you pin. Rearrange it to suit how you review.",
  },
  {
    targetId: "trades",
    title: "Your trades",
    body: "Every trade you import or add lands here. Open one to see executions, attach notes, and review what worked.",
  },
  {
    targetId: "calendar",
    title: "Calendar view",
    body: "A month-by-month heatmap of daily P&L. Click any day to jump straight to that day's trades.",
  },
  {
    targetId: "notes",
    title: "Journal & notes",
    body: "Record your thinking — daily plans, lessons, and screenshots — so your edge compounds over time.",
  },
  {
    targetId: "import",
    title: "Import statements",
    body: "Bring trades in from your broker by uploading a statement. The import wizard walks you through mapping and review.",
  },
];

export interface TourContextValue {
  isActive: boolean;
  step: number;
  totalSteps: number;
  activeTargetId: string | null;
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  // Finish the tour (last "Done") — records completion so the dashboard card
  // stops auto-prompting.
  finishTour: () => void;
  // Abandon the tour early (skip / Escape) — also records completion so it does
  // not nag, but is always re-launchable from Settings.
  skipTour: () => void;
}

// No-op default so consumers (Sidebar, Settings) can read the context safely
// even when rendered without a provider — e.g. in their own unit tests.
const NOOP_TOUR: TourContextValue = {
  isActive: false,
  step: 0,
  totalSteps: TOUR_STEPS.length,
  activeTargetId: null,
  startTour: () => {},
  nextStep: () => {},
  prevStep: () => {},
  finishTour: () => {},
  skipTour: () => {},
};

export const TourContext = createContext<TourContextValue>(NOOP_TOUR);

export function useTour(): TourContextValue {
  return useContext(TourContext);
}
